// Intent score for table rows — the same number the lead page shows, cached so
// sorting the whole directory is affordable.
//
// Two costs to respect:
//   1. calibratedScore() → baseSignal() runs three regex passes over the packed
//      `projects` blob. Measured: ~206ms for 37k rows on this hardware, and it
//      scales linearly with blob size.
//   2. DataTable's comparator calls sortValue TWICE per comparison — measured
//      711,584 calls for 37k rows (~19x per row). So the score must be O(1) on
//      the hot path, not merely memoized-per-object.
//
// Hence: keyed by the row's STABLE key (keyOf), not by object identity. The
// leads cache re-parses decrypted region blobs on every mount and after every
// sync, handing the table brand-new objects each time — an identity-keyed cache
// went cold on every remount even though the score depends only on stable
// fields.
import { calibratedScore, calibration } from './intel.js'
import { keyOf } from './companyMeta.js'
import { currentScope } from './userScope.js'

const cache = new Map()
let cacheSig = ''

// A calibration shifts scores through winRate/confidence AND through its
// per-bucket counts, so the bucket state has to be part of the key: a labeled
// company's attributes can change (a Sync adds an email) while outcomes,
// winRate and confidence all stay put.
// The signed-in account leads the signature. This cache is a module singleton
// that survives a sign-out, and `acc` below is a LOSSY digest — it folds in
// each bucket key's LENGTH, not its content, so two accounts with different
// calibrations can collide (`ageBand:new` and `email:false` are both 11 chars)
// and the second would be served the first's cached scores. Scoping the
// signature retires that whole class instead of chasing the hash quality.
function signatureOf(calib) {
  const who = currentScope()
  if (!calib || !calib.outcomes) return `${who}|none`
  const buckets = calib.buckets || {}
  let acc = 0
  for (const k of Object.keys(buckets)) {
    const b = buckets[k]
    // Cheap order-independent digest of the bucket table.
    acc = (acc + k.length * 31 + (b?.n || 0) * 7 + (b?.pos || 0) * 13) % 2147483647
  }
  return `${who}|${calib.outcomes}:${calib.winRate}:${calib.shrink}:${acc}`
}

// Pass the same `calib` for a whole pass (calibration() reads localStorage).
export function intentScoreOf(company, calib) {
  if (!company) return 0
  const sig = signatureOf(calib)
  if (sig !== cacheSig) {
    cache.clear()
    cacheSig = sig
  }
  const key = keyOf(company)
  if (key) {
    const hit = cache.get(key)
    if (hit !== undefined) return hit
  }
  const score = calibratedScore(company, calib)?.score ?? 0
  if (key) cache.set(key, score)
  return score
}

// Warm every score in one linear pass before a sort touches them — turns ~19
// cold computes per row into exactly one, and keeps the comparator O(1).
export function warmIntentScores(companies, calib) {
  if (!companies?.length) return
  const sig = signatureOf(calib)
  if (sig !== cacheSig) {
    cache.clear()
    cacheSig = sig
  }
  for (const c of companies) {
    const key = keyOf(c)
    if (key && cache.has(key)) continue
    const score = calibratedScore(c, calib)?.score ?? 0
    if (key) cache.set(key, score)
  }
}

// One tier definition, shared by the leads table and the lead page so the chip
// on a row can never disagree with the chip on its detail view.
export const INTENT_TIERS = [
  {
    min: 70,
    label: 'HIGH',
    full: 'HIGH INTENT',
    chip: 'bg-[#f0fdf4] text-[#15803d] dark:bg-[rgba(21,128,61,0.18)] dark:text-[#4ade80]',
    bar: 'bg-[#22c55e]',
  },
  {
    min: 45,
    label: 'WARM',
    full: 'WARM',
    chip: 'bg-[#fefce8] text-[#a16207] dark:bg-[rgba(161,98,7,0.18)] dark:text-[#fbbf24]',
    bar: 'bg-[#f59e0b]',
  },
  {
    min: 0,
    label: 'COLD',
    full: 'COLD',
    chip: 'bg-panel text-dim',
    bar: 'bg-[color:var(--muted)]',
  },
]

export function intentTier(score) {
  const n = Number(score) || 0
  return INTENT_TIERS.find((t) => n >= t.min) || INTENT_TIERS[INTENT_TIERS.length - 1]
}

// Convenience for callers that don't already hold a calibration.
export function freshCalibration() {
  return calibration()
}
